import { useEffect, useState } from 'react';
import { registerModalBackHandler } from '../../lib/native';
import { AddressInput } from '../ui/AddressInput';
import { Button, PhotoGalleryField } from '../design-system';
import { getPlacePhotos, photoFields, uploadPlacePhoto } from '../../lib/placePhotos';
import type { ItineraryPlace } from '../../types';

interface PlaceEditorModalProps {
  place: ItineraryPlace;
  isNew: boolean;
  categoryOptions: { slug: string; name: string }[];
  hostId: string;
  onSave: (place: ItineraryPlace) => void;
  onDelete?: () => void;
  onSaveToLibrary: (place: ItineraryPlace) => void | Promise<void>;
  onClose: () => void;
}

/**
 * Modal form for one itinerary event. Slides up from the bottom on mobile,
 * centred on desktop. Used by the host itinerary editor for both adding a new
 * event and editing an existing one.
 */
export function PlaceEditorModal({
  place, isNew, categoryOptions, hostId, onSave, onDelete, onSaveToLibrary, onClose,
}: PlaceEditorModalProps) {
  const [draft, setDraft] = useState<ItineraryPlace>(place);
  const [savedToLib, setSavedToLib] = useState(false);

  function set<K extends keyof ItineraryPlace>(k: K, v: ItineraryPlace[K]) {
    setDraft((d) => ({ ...d, [k]: v }));
  }

  // Android hardware back button closes this modal instead of navigating
  // the route underneath it.
  useEffect(() => registerModalBackHandler(onClose), [onClose]);

  async function handleSaveToLibrary() {
    await onSaveToLibrary(draft);
    setSavedToLib(true);
    setTimeout(() => setSavedToLib(false), 2500);
  }

  const inputClass = 'rounded-md border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none';

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end md:items-center justify-center" onClick={onClose}>
      <div
        className="bg-app-card md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-lg max-h-dvh-92 overflow-y-auto pb-safe-or-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-app-card z-10 px-5 pt-5 pb-3 border-b border-app-border flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-app-fg">{isNew ? 'Add event' : 'Edit event'}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-app-bg border border-app-border text-app-muted flex items-center justify-center hover:text-app-fg shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-5 flex flex-col gap-4">
          {/* Photo */}
          <div className="flex flex-col gap-2">
            <PhotoGalleryField
              photos={getPlacePhotos(draft)}
              onChange={(photos) => setDraft((d) => ({ ...d, ...photoFields(photos) }))}
              onUpload={(file) => uploadPlacePhoto(hostId, file)}
              inputClassName={`${inputClass} py-1.5`}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-app-fg-2">Name</label>
              <input value={draft.name} onChange={(e) => set('name', e.target.value)} placeholder="Taberna Belga" className={inputClass} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-app-fg-2">Category</label>
              <select value={draft.category} onChange={(e) => set('category', e.target.value)} className={inputClass}>
                {categoryOptions.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <AddressInput
              label="Address"
              value={draft.address ?? ''}
              onChange={(v) => set('address', v || null)}
              onGeocode={(lat, lng) => setDraft((d) => ({ ...d, latitude: lat, longitude: lng }))}
              placeholder="Start typing to search…"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-app-fg-2">Description</label>
            <textarea value={draft.description} onChange={(e) => set('description', e.target.value)}
              rows={3} placeholder="What makes this place special — the vibe, the food, why you love it…"
              className={`${inputClass} resize-none`} />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-app-fg-2">Local tip</label>
            <textarea value={draft.tip ?? ''} onChange={(e) => set('tip', e.target.value || null)}
              rows={2} placeholder="Ask for the house wine, sit upstairs for the view, go before 7pm…"
              className={`${inputClass} resize-none`} />
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!draft.highlight} onChange={(e) => set('highlight', e.target.checked)}
              className="rounded border-app-border bg-app-bg text-accent" />
            <span className="text-app-fg-2">Mark as a highlight</span>
          </label>

          {/* Actions */}
          <div className="flex items-center justify-between gap-3 pt-2 border-t border-app-border">
            <div className="flex items-center gap-3">
              <button onClick={() => void handleSaveToLibrary()} disabled={!draft.name.trim()}
                className="text-xs text-violet/70 hover:text-violet transition-colors disabled:opacity-40">
                {savedToLib ? 'Saved ✓' : 'Save to library'}
              </button>
              {!isNew && onDelete && (
                <button onClick={onDelete} className="text-xs text-crimson/60 hover:text-crimson transition-colors">Remove</button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={onClose} className="text-sm text-app-muted hover:text-app-fg">Cancel</button>
              <Button onClick={() => onSave(draft)} disabled={!draft.name.trim()}>
                {isNew ? 'Add event' : 'Done'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlaceEditorModal;
